// src/lib/permissions/store/permissionStore.ts
// Modern Zustand store for permission management

import { create } from 'zustand';
import { PermissionMap, UserGroup } from '../../../types/access';
import { PermissionEngine } from '../core/engine';
import { PERMISSION_MODULES, ModuleCategory, AccessLevel } from '../core/modules';

interface PermissionState {
  // Core state
  permissions: PermissionMap | null;
  userGroups: UserGroup[];
  currentUser: string | null;
  loading: boolean;
  error: string | null;
  noGroups: boolean;
  environment: string | null;
  loadTimestamp: string | null;

  // Internal engine
  engine: PermissionEngine | null;
}

interface PermissionActions {
  // State management
  setPermissions: (permissions: PermissionMap) => void;
  setUserGroups: (userGroups: UserGroup[]) => void;
  setCurrentUser: (username: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setNoGroups: (noGroups: boolean) => void;
  setEnvironmentInfo: (environment: string, loadTimestamp: string) => void;
  clearError: () => void;
  reset: () => void;

  // Permission checking
  hasPermission: (permissionKey: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;  
  // Module access
  canAccessModule: (moduleName: string) => boolean;
  canViewModule: (moduleName: string) => boolean;
  canUpdateModule: (moduleName: string) => boolean;
  getModuleAccessLevel: (moduleName: string) => AccessLevel;
  
  // Admin access
  hasAdminAccess: () => boolean;
  
  // Module information
  getAccessibleModules: () => any[];
  getModulesByCategory: (category: ModuleCategory) => any[];
  getAllModules: () => any[];
  
  // Advanced features
  getPermissionStats: () => any;
  validatePermissions: () => any;
  getAccessSummary: () => Record<string, AccessLevel>;
  getCacheStats: () => any;
  clearCache: () => void;
  
  // User management
  getUserGroup: () => UserGroup | null;
}

type PermissionStore = PermissionState & PermissionActions;

export const usePermissionStore = create<PermissionStore>((set, get) => ({
  // Initial state
  permissions: null,
  userGroups: [],
  currentUser: null,
  loading: false,
  error: null,
  noGroups: false,
  environment: null,
  loadTimestamp: null,
  engine: null,

  // State management actions
  setPermissions: (permissions: PermissionMap) => {
    const engine = new PermissionEngine(permissions, PERMISSION_MODULES);
    
    set({ permissions, engine });
    
    // Development logging
    if (process.env.NODE_ENV === 'development') {
      const stats = engine.getPermissionStats();
      // Permissions Updated - stats available
    }
  },
  setUserGroups: (userGroups: UserGroup[]) => set({ userGroups }),
  setCurrentUser: (username: string) => set({ currentUser: username }),
  setLoading: (loading: boolean) => set({ loading }),
  setError: (error: string | null) => set({ error }),
  setNoGroups: (noGroups: boolean) => set({ noGroups }),
  setEnvironmentInfo: (environment: string, loadTimestamp: string) => 
    set({ environment, loadTimestamp }),
  clearError: () => set({ error: null }),
  
  reset: () => set({
    permissions: null,
    userGroups: [],
    currentUser: null,
    loading: false,
    error: null,
    noGroups: false,
    environment: null,
    loadTimestamp: null,
    engine: null
  }),

  // Permission checking methods
  hasPermission: (permissionKey: string) => {
    const { engine, noGroups } = get();
    if (noGroups || !engine) return false;
    return engine.hasPermission(permissionKey);
  },

  hasAnyPermission: (permissions: string[]) => {
    const { engine, noGroups } = get();
    if (noGroups || !engine) return false;
    return engine.hasAnyPermission(permissions);
  },
  hasAllPermissions: (permissions: string[]) => {
    const { engine, noGroups } = get();
    if (noGroups || !engine) return false;
    return engine.hasAllPermissions(permissions);
  },

  // Module access methods
  canAccessModule: (moduleName: string) => {
    const { engine, noGroups } = get();
    if (noGroups || !engine) return false;
    return engine.canAccessModule(moduleName);
  },

  canViewModule: (moduleName: string) => {
    const { engine, noGroups } = get();
    if (noGroups || !engine) return false;
    return engine.canViewModule(moduleName);
  },

  canUpdateModule: (moduleName: string) => {
    const { engine, noGroups } = get();
    if (noGroups || !engine) return false;
    return engine.canUpdateModule(moduleName);
  },

  getModuleAccessLevel: (moduleName: string) => {
    const { engine, noGroups } = get();
    if (noGroups || !engine) return 'none';
    return engine.getModuleAccessLevel(moduleName);
  },

  // Admin access
  hasAdminAccess: () => {
    const { engine, noGroups } = get();
    if (noGroups || !engine) return false;
    return engine.hasAdminAccess();
  },
  // Module information
  getAccessibleModules: () => {
    const { engine } = get();
    if (!engine) return [];
    return engine.getAccessibleModules();
  },

  getModulesByCategory: (category) => {
    const { engine } = get();
    if (!engine) return [];
    return engine.getModulesByCategory(category);
  },

  getAllModules: () => PERMISSION_MODULES,

  // Advanced features
  getPermissionStats: () => {
    const { engine } = get();
    if (!engine) {
      return {
        totalPermissions: 0,
        grantedPermissions: 0,
        accessibleModules: 0,
        adminAccess: false,
        moduleStats: {}
      };
    }
    return engine.getPermissionStats();
  },

  validatePermissions: () => {
    const { engine } = get();
    if (!engine) {
      return { isValid: false, issues: ['Engine not initialized'], suggestions: [] };
    }
    return engine.validatePermissions();
  },

  getAccessSummary: () => {
    const { engine } = get();
    if (!engine) return {};
    return engine.getPermissionStats().moduleStats;
  },
  getCacheStats: () => {
    const { engine } = get();
    if (!engine) return { size: 0, hitCount: 0, missCount: 0, hitRate: 0 };
    return engine.getCacheStats();
  },

  clearCache: () => {
    const { engine } = get();
    if (engine) engine.clearCache();
  },

  // User management
  getUserGroup: () => {
    const { userGroups, noGroups, currentUser } = get();
    
    if (noGroups || userGroups.length === 0) return null;
    
    if (currentUser) {
      // Dynamic user group detection based on Azure/Entra groups
      // This should be handled by the backend API call that matches user's Azure groups
      // to the application's user groups
      
      // For now, return the first group if user has access to groups
      // The proper logic should be in the backend API response
      if (userGroups.length === 1) {
        return userGroups[0];
      }
      
      // If multiple groups, the backend should determine the primary group
      // or we should have a different strategy based on your business logic
      console.warn('Multiple user groups found, returning first group. Consider implementing group priority logic.');
      return userGroups[0] || null;
    }
    
    return null;
  }
}));

// Development helpers
if (process.env.NODE_ENV === 'development') {
  (window as any).permissionStore = usePermissionStore;
  (window as any).PERMISSION_MODULES = PERMISSION_MODULES;
}